import { passwordStrength, type StrengthLevel } from './passwordStrength';

const LEVELS: Record<Exclude<StrengthLevel, 0>, { label: string; bar: string }> = {
  1: { label: 'Fragile comme un bambou sec', bar: 'bg-danger-500' },
  2: { label: 'Ça pousse… continue !', bar: 'bg-sun-500' },
  3: { label: 'Belle tige de bambou !', bar: 'bg-bamboo-400' },
  4: { label: 'Costaud comme un panda !', bar: 'bg-bamboo-500' },
};

export type PasswordStrengthMeterProps = {
  password: string;
  className?: string;
};

/**
 * Jauge de force du mot de passe : 4 barres colorées danger → soleil → bambou
 * + label fun, annoncé poliment aux lecteurs d'écran. Indicative seulement
 * (ne bloque pas la soumission). Le label reste en texte neutre (contraste
 * AA dans les deux thèmes) ; la couleur est portée par les barres.
 */
export function PasswordStrengthMeter({ password, className }: PasswordStrengthMeterProps) {
  const level = passwordStrength(password);
  const info = level === 0 ? null : LEVELS[level];

  return (
    <div className={className ? `mt-2 ${className}` : 'mt-2'}>
      <div className="flex gap-1.5" aria-hidden="true" data-testid="strength-bars">
        {([1, 2, 3, 4] as const).map((i) => (
          <span
            key={i}
            data-testid="strength-bar"
            data-active={info !== null && i <= level ? 'true' : 'false'}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-base ease-fun ${
              info !== null && i <= level ? info.bar : 'bg-ink-600'
            }`}
          />
        ))}
      </div>
      <p className="mt-1.5 min-h-[1rem] text-xs text-ink-400" aria-live="polite">
        {info ? (
          <>
            Force (indicatif) : <span className="font-semibold text-ink-100">{info.label}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}
